/**
 * Восстановление: визард, а не кнопка.
 *
 * Форма выбрана не ради красоты. Восстановление — это запись поверх чужой
 * машины, и человек должен на каждом шаге видеть, что именно произойдёт:
 * какой снимок, куда, сколько это весит и влезает ли. Шаг, на котором ответить
 * нечем, обрывает процедуру — лучше остановиться на выборе места, чем
 * выяснить на середине, что места не хватило.
 *
 * Порядок шагов:
 *
 *   1. что   — снимок и источники внутри него;
 *   2. куда  — приёмник и каталог бандла;
 *   3. смета — сколько нужно, сколько свободно, влезает ли;
 *   4. пуск  — подтверждение вторым фактором и выдача тикета.
 */
import { useEffect, useState } from 'react';
import { blocksStart } from '../../shared/domain/restore-fit.ts';
import { draft, keepDraft } from './restore-draft.ts';
import { useQuery } from '../transport/hooks.ts';
import { useTopic } from '../transport/live.ts';
import { request } from '../transport/socket.ts';
import { bytes, count, plural } from '../lib/format.ts';
import { counterWord, tell } from './restore-counter.ts';
import {
  agoWords, forHuman, useEased, useFlowShape, useLastWriting, useNow, useRecentRate,
  useSlowRate,
} from '../lib/rate.ts';
import { Sparkline } from '../components/Sparkline.tsx';
import { SpeedGauge } from '../components/SpeedGauge.tsx';
import { Toggle } from '../components/Toggle.tsx';
import { Confirm, useConfirm } from '../components/Confirm.tsx';
import { RestoreReport } from '../components/RestoreReport.tsx';
import { RestoreRequests } from '../components/RestoreRequests.tsx';
import { AGENTS_TOPIC, RESTORES_TOPIC } from '../../shared/protocol/agents.ts';
import type {
  AgentEvent, AgentsSnapshot, RestoreEvent, RestoreView,
} from '../../shared/protocol/agents.ts';

interface SiteRow {
  hostId: string;
  siteId: string;
  label: string;
  snapshots: number;
  lastAt: string | null;
  /** Машина объявляет этот сайт и сейчас. Нет — снимки есть, а сайта нет. */
  declared: boolean;
}

interface SnapshotRow {
  snapshotId: string;
  state: string;
  finishedAt: string | null;
  sources?: string[];
  sourceSizes?: { id: string; files?: number; bytes: number }[] | null;
  /** Куда какой источник ляжет внутри каталога: путь из манифеста снимка. */
  sourcePaths?: { id: string; path: string }[] | null;
  totals?: { files?: number; bytes?: number; chunks?: number; dirs?: number };
}

interface Plan {
  /** Можно ли просить приёмник поднять базу и службы — и почему такой ответ. */
  deploy: { allowed: boolean; why: string };
  /** Что приёмник предлагает не грузить в базу — его знание, не наше. */
  skippable: {
    id: string; bytes: number | null; byDefault: boolean; why: string; tables: string[];
  }[];
  snapshotId: string;
  sourceSiteId: string;
  sourceHostId: string;
  sources: { id: string; files: number | null; bytes: number }[];
  totals: { files: number; dirs: number; bytes: number; chunks: number };
  target: {
    hostId: string; label: string; online: boolean; freeBytes: number | null;
    topologySuspect?: {
      why: string; at: string; wasSites: string[]; nowSites: string[];
    };
    dirs: {
      path: string; entries: number; writable?: boolean;
      /** Чью копию сюда принимают — по объявлению самой машины. */
      siteId?: string;
      /** Зачем сюда разрешено, словами владельца машины. */
      why?: string;
      /** Каталог занят развёрнутым проектом, а не просто непуст. */
      looksLikeSite?: boolean;
      /** Каталог занят идущим восстановлением: номер работы и её начало. */
      busy?: { restoreId: string; since: string };
    }[];
  };
  intoEntries: number | null;
  already?: {
    restoreId: string; snapshotId: string; sameSnapshot: boolean; covers: boolean;
    at: string; bytes: number; files: number | null;
  };
  fitsCertain: boolean;
  services: {
    source: { id: string; role?: string; engine?: string; provided: string; where?: string }[];
    target: { id: string; role?: string; engine?: string; provided: string; where?: string }[];
    missing: string[];
    differs: { id: string; source: string; target: string }[];
  } | null;
  intoBytes: number | null;
  intoBytesAt: string | null;
  freeAfter: number | null;
  mode?: 'resume' | 'mirror' | 'prune';
  needBytes: number;
  fits: boolean | null;
  estimate: { seconds: number; bytesPerSec: number; basis: string } | null;
}


const STATE_WORD: Record<string, string> = {
  granted: 'ключи выданы, ждём приёмник',
  running: 'идёт',
  ok: 'готово',
  failed: 'сбой',
  cancelled: 'остановлено',
};



/**
 * Примет ли приёмник такой объём.
 *
 * Отдельным показателем, а не строкой в смете: этот ответ решает, начинать ли
 * вообще, и он должен быть виден раньше, чем человек дойдёт до красной кнопки.
 *
 * Три состояния, и третье — не отговорка. «Приёмник не сообщает» значит ровно
 * это: агент такой машины ещё не шлёт свободное место, и обещать «влезет» нам
 * не из чего. Проверит он сам перед первой записью — но узнаем мы об этом
 * позже, чем хотелось бы.
 */
/**
 * Что считает счётчик `files` в этой фазе.
 *
 * У приёмника одно поле на все фазы, и смысл у него разный: в `manifest` это
 * разобранные записи перечня, в `compare` — сверенные файлы, в `chunks` —
 * полученные локации, и только в `restore` это действительно записанные файлы
 * (222). Подписывать всё словом «файлов» — значит показывать «0 из 4 058 965
 * файлов», когда речь о чанках, и менять итог на глазах при каждой смене
 * фазы. Владелец так и увидел: «0 из меняется количеством файлов».
 */
/**
 * Из какого источника этот файл — по совпадению начала пути.
 *
 * Самое длинное совпадение, а не первое: `magento2/pub/media` начинается с
 * `magento2`, и первое попавшееся назвало бы медиатеку кодом.
 */
function sourceOf(
  path: string,
  paths: { id: string; path: string }[] | null,
): string | null {
  if (!paths?.length) return null;
  let best: { id: string; path: string } | null = null;
  for (const s of paths) {
    const prefix = s.path === '.' ? '' : s.path.replace(/\/+$/, '');
    if (prefix && !path.startsWith(prefix)) continue;
    if (!best || prefix.length > (best.path === '.' ? 0 : best.path.length)) best = s;
  }
  return best?.id ?? null;
}


/**
 * Считать ли замер объёма устаревшим.
 *
 * Полчаса — не круглое число ради круглости: приёмник пересчитывает объём, если
 * тот старше десяти минут, значит в норме отметке не больше десяти минут плюс
 * промежуток между отчётами. Всё, что заметно больше, означает, что пересчёт не
 * шёл — агент был занят или лежал, — и полагаться на число уже нельзя.
 */
function staleAge(at: string): boolean {
  return Date.now() - new Date(at).getTime() > 30 * 60_000;
}

function Capacity({ plan }: { plan: Plan }): React.JSX.Element {
  if (plan.target.freeBytes == null) {
    return (
      <div className="capacity capacity--unknown">
        <div className="capacity__word">приёмник не сообщает свободного места</div>
        <p className="muted">
          Проверит сам перед первой записью. Чтобы видеть это здесь, агент
          должен слать тома машины — просьба отправлена.
        </p>
      </div>
    );
  }

  const free = plan.target.freeBytes;
  const need = plan.needBytes;
  // Место после разворота считает сервер: там известен режим, а от режима
  // зависит, освобождается ли содержимое каталога.
  const after = plan.freeAfter ?? free - need;
  /*
   * «Не влезает» — это ответ, а «похоже, не влезет» — предупреждение.
   *
   * Точный ответ у нас есть только для пустого каталога. Во всех остальных
   * случаях мы считаем по верхней границе — весу снимка, — а сколько придётся
   * записать на самом деле, выясняется у приёмника, когда он сверит снимок с
   * диском. Разница огромна: ночной снимок отличается от развёрнутого вчера на
   * полторы тысячи файлов из четырёх миллионов, а верхняя граница считает их
   * все.
   *
   * Поэтому неточный отрицательный ответ НЕ запирает кнопку. Запрещать работу
   * на основании того, чего мы не знаем, — худший из способов беречь: приёмник
   * всё равно считает место сам и отказывается писать в переполненный диск,
   * и его отказ основан на сверке, а наш — на предположении.
   */
  const fits = after > 0;
  const doubt = !fits && !plan.fitsCertain;
  const share = Math.min(1, free > 0 ? need / (free + Math.max(0, free - after - need)) : 1);
  /*
   * Есть ли в каталоге что-то, чего смета не учитывает.
   *
   * `needBytes` — вес выбранного в снимке, то есть ВЕРХНЯЯ граница записи: то,
   * что уже лежит и совпало, приёмник не перекачивает. Сколько именно совпало,
   * знает только он, сверив дерево. Пока он не сообщает даже размера каталога,
   * честно сказать «не больше» — единственное, что можно.
   */
  const dirty = (plan.intoEntries ?? 0) > 0 && plan.intoBytes == null;

  return (
    <div className={`capacity${fits ? '' : doubt ? ' capacity--doubt' : ' capacity--bad'}`}>
      <div className="capacity__word">
        {fits ? 'приёмник примет' : doubt ? 'по верхней границе не влезает' : 'НЕ ВЛЕЗАЕТ'}
        <span className="muted">
          {' '}— нужно {dirty || plan.already ? 'не больше ' : ''}{bytes(need)} из {bytes(free)} свободных
        </span>
      </div>
      <div className="capacity__bar" role="img"
           aria-label={`занято ${Math.round(share * 100)} процентов свободного`}>
        <span className="capacity__fill" style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
      <p className="muted">
        {fits
          ? `останется ${bytes(Math.max(0, after))}`
          : doubt
            ? `по весу снимка не хватило бы ${bytes(-after)} — но столько писать не придётся, `
              + 'если дерево уже на месте'
            : `не хватает ${bytes(-after)}`}
      </p>
      {doubt && (
        <p className="muted">
          Сколько писать на самом деле, приёмник узнает, сверив снимок с диском —
          это первая фаза работы, и идёт она до первого записанного байта. Место
          он проверяет сам и в переполненный диск не пишет: его отказ будет
          основан на сверке, а наш — на предположении. Начать можно.
        </p>
      )}
      {plan.intoBytes != null && plan.intoBytes > 0 && (
        <p className="muted">
          В каталоге лежит {bytes(plan.intoBytes)}
          {plan.mode === 'prune'
            ? ' — они освободятся: каталог очищается перед записью.'
            : ' — они остаются на месте и в свободное место не вернутся.'}
          {/*
            Возраст замера показываем всегда, а не только когда он велик.
            Приёмник считает объём в стороне и честно говорит когда — читать
            только число значит выдавать снятое час назад за снятое сейчас.
            Каталог меняется, пока идёт восстановление и работает магазин.
          */}
          {plan.intoBytesAt && (
            <span className={staleAge(plan.intoBytesAt) ? ' dns__why' : ''}>
              {' '}· замерено {agoWords(Date.now() - new Date(plan.intoBytesAt).getTime())}
              {staleAge(plan.intoBytesAt) ? ', с тех пор могло измениться' : ''}
            </span>
          )}
        </p>
      )}
      {/*
        Повторный разворот — главное, что стоит сказать про это место: дерево
        уже здесь, и «нужно 247 ГБ» это вес снимка, а не работа.
      */}
      {plan.already && (
        <p className="muted">
          {/*
            Совпадение ищется по САЙТУ, а не по снимку: ночной снимок отличается
            от вчерашнего на полторы тысячи файлов из четырёх миллионов, и
            считать дерево чужим только потому, что имя снимка другое, — значит
            снова показать 247 ГБ работы там, где её единицы гигабайт.
          */}
          {plan.already.sameSnapshot ? 'Этот снимок' : 'Соседний снимок этого сайта'} сюда
          уже развёрнут{' '}
          {new Date(plan.already.at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })} —
          {' '}{bytes(plan.already.bytes)}
          {plan.already.files != null && <> и {count(plan.already.files)} файлов</>} лежали
          на месте, когда работа закончилась. Заново пойдёт только изменившееся;
          сколько именно, знает приёмник, сверив дерево.
          {!plan.already.covers && (
            <> Но она не покрывает нынешний выбор — тогда разворачивали не всё
            из того, что просят сейчас, — поэтому в смете её итог не учтён.</>
          )}
        </p>
      )}

      {dirty && !plan.already && (
        <p className="muted">
          В каталоге уже что-то есть, и сколько из нужного там уже лежит, знает
          только приёмник: совпавшее он не перекачивает. Смета показывает
          верхнюю границу — ошибиться в эту сторону безопаснее.
        </p>
      )}
    </div>
  );
}

/**
 * Наблюдение за идущим восстановлением.
 *
 * Тот же прибор, что у прохода, и это не экономия: работа та же, только с
 * обратным знаком — скачано из хранилища вместо прочитано с диска, записано на
 * диск вместо отправлено в хранилище. Глазу, привыкшему к одному экрану, не
 * надо переучиваться; подписи под числами меняются, шкала и метки — нет.
 *
 * Доля считается по файлам, а если их число неизвестно — по байтам. Пока не
 * известно ни то ни другое, полоса бежит штриховкой: доля от неизвестного
 * хуже, чем честное «идёт, сколько — неизвестно».
 */
function RestoreProgress({ r, sourcePaths }: {
  r: RestoreView;
  /**
   * Куда какой источник ложится — из манифеста снимка, если он под рукой.
   *
   * Под рукой он только у выбранного в визарде снимка: у остальных карточек
   * списка его никто не читал. Поэтому необязателен, и без него строка пути
   * остаётся такой, какой была.
   */
  sourcePaths?: { id: string; path: string }[] | null;
}): React.JSX.Element {
  const p = r.progress;
  // Часы тикают сами: без них счётчик молчания застывал на «сейчас» ровно
  // тогда, когда приёмник замолкал, — событий-то больше не приходит.
  const now = useNow(r.state === 'running');
  const silentFor = now - new Date(r.lastActivityAt).getTime();
  const live = r.state === 'running' && silentFor < 10_000;
  /*
   * Тишина в начале и тишина посередине — разные вещи.
   *
   * Прежде чем скачать первый байт, приёмник читает перечень снимка: четыре
   * миллиона строк, две-три минуты на боевом. Кадров хода в это время нет, и
   * экран показывал «идёт, 0 из 4 180 736» с застывшим счётчиком — то есть
   * ровно то, что выглядит зависшим. Владелец так и решил, остановил работу
   * и запустил заново — и попал в ту же подготовку.
   *
   * Поэтому «молчит» говорится только про то, что УЖЕ шло и перестало.
   * Началом считаем первый скачанный байт или первый файл: до этого честное
   * слово — «готовится».
   */
  /*
   * Фаза, если приёмник её называет (сообщение 222).
   *
   * Три подготовки перед первым байтом — разбор перечня, сверка с диском,
   * получение карты — длятся минуты каждая и снаружи выглядят одинаково.
   * Раньше мы различали их одним признаком «пошли ли байты», и этого было
   * мало: сверка на непустом приёмнике не трогает сеть ВОВСЕ, честно показывая
   * ноль, а экран называл бы это молчанием и пугал.
   */
  const phase = p.phase ?? null;
  const PHASES: Record<string, string> = {
    // `survey` — их слово (234): перечень снимка и опись приёмника читаются
    // ОДНОВРЕМЕННО, поэтому назвать фазу одним из двух было бы неправдой.
    survey: 'читает перечень снимка и опись приёмника',
    manifest: 'читает перечень снимка',
    compare: 'сверяет с тем, что уже на диске',
    chunks: 'получает карту чанков',
    restore: 'разворачивает',
    /*
     * Круг проверки после записи — своим словом (их 260).
     *
     * Они называли `survey` две разные работы: первичную опись приёмника и
     * пересчёт после записи. Отсюда в разборе стояли две строки с одинаковой
     * подписью — «подготовка» и «запись», обе «снимаю опись заново».
     *
     * Наш словарь открыт с того дня, как строгий список отверг боевые кадры и
     * похоронил работу на четверть терабайта: незнакомое слово мы показываем
     * как есть. Значит новое слово нам ничего не стоит, а разбор становится
     * читаемым — о чём им и сказали.
     */
    verify: 'проверяет, что получилось',
    // Развёртывание, их 274: шесть частей, каждая докладывает о себе.
    prepare: 'готовит конфигурацию',
    services: 'поднимает службы',
    backup: 'снимает запасной дамп базы',
    import: 'заливает базу из снимка',
    finalize: 'доводит после дампа',
    restart: 'пересоздаёт контейнеры',
    // Единственная фаза, которая проверяет не нашу работу, а магазин.
    check: 'проверяет, что сайт отдаёт сайт',
  };
  /** Фазы, в которых байты не идут по устройству, а не по беде. */
  const PREP_PHASES = new Set(['survey', 'manifest', 'compare', 'chunks', 'verify']);
  /*
   * Незнакомая фаза — не повод молчать.
   *
   * Слов у приёмника может стать больше, чем у нас: он назвал фазу, которой в
   * нашем списке не было, и строгая проверка отвергла кадры целиком. Теперь
   * незнакомое слово показываем как есть, а работой считаем всё, что не
   * похоже на подготовку.
   */
  /*
   * Что показывать — решаем по байтам, а не по слову.
   *
   * Приёмник прислал `phase: restore` и при этом `note: «сверяю с описью
   * приёмника»`, ноль записанных байт и счётчик в 6 238 045 записей — больше,
   * чем файлов в снимке. Экран поверил слову и нарисовал работу: «6 238 045 из
   * 6 238 045 файлов», «восстановлено 0 из 247 ГБ», стрелка в нуле и дуга
   * «файлов 100%».
   *
   * Слово подписывает, а решает измеримое: пока не записан ни байт и не взят
   * ни один пак — идёт подготовка, чем бы её ни называли. Своё слово приёмника
   * при этом показываем: оно точнее нашего словаря.
   */
  /*
   * Заголовок наш, подробность их.
   *
   * Примечание приёмника — фраза с числами («перечень снимка 4 222 824 из
   * 4 222 824, опись приёмника 950 272»). Как заголовок она не годится: длинная,
   * переносится и повторяет то, что и так стоит рядом цифрами. Как пояснение —
   * годится, и её место внизу, одной строкой.
   */
  const phaseWord = phase ? PHASES[phase] ?? phase : null;
  const moved = (p.bytesWritten ?? 0) > 0 || (p.packsDone ?? 0) > 0
    || p.bytesRead > 0 || p.bytesUploaded > 0;
  /*
   * Подготовка бывает и ПОСЛЕ записи.
   *
   * Приёмник снимает опись заново и пересчитывает план — до четырёх кругов,
   * пока расхождений не останется (234). Вторая `survey` под конец работы —
   * это проверка, а не откат, и показывать её надо так же спокойно, как
   * первую: прибор скорости там снова нечем кормить.
   */
  const inPrep = phase ? PREP_PHASES.has(phase) : !moved;
  const rechecking = inPrep && moved;
  /*
   * Работа началась — и больше не «начинается» заново.
   *
   * Приёмник после записи снимает опись ещё раз и так до четырёх кругов. Фаза
   * при этом снова становится `survey`, и экран, решавший по одной фазе,
   * перекидывался с прибора на подготовку и обратно каждые несколько секунд.
   * Владелец на это и указал: «что тут понять? что именно происходит?» —
   * понять было нельзя, потому что экран менялся целиком по два раза в минуту.
   *
   * Начало работы необратимо: байты записаны, и проверочный круг — часть той
   * же работы, а не новая подготовка.
   */
  const begun = moved;
  /*
   * Файлы кончились, дальше идёт то, о чём приёмник не рассказывает.
   *
   * После раскладки у него 31 шаг подготовки, снятие запасного дампа, заливка
   * на полтора часа и пересоздание контейнеров. Кадров хода в это время нет
   * вовсе — и экран объявлял тишину бедой: «Приёмник молчит. Работа стоит».
   *
   * Работа не стоит. Просто самый долгий её этап у нас не виден, и честно
   * сказать об этом лучше, чем пугать тем, чего нет.
   */
  const filesDone = p.filesTotal != null && p.filesTotal > 0 && p.filesDone >= p.filesTotal;
  const nothingLeft = p.bytesPending != null && p.bytesPending === 0;
  /*
   * Развёртывание узнаём по фазе, а не по тишине.
   *
   * Тишина была признаком от бедности: приёмник об этом этапе не говорил
   * вовсе. Теперь говорит — шестью фазами, — и признак стал прямым. Тишину
   * оставляем запасным ответом: сборка приёмника может быть старой, и терять
   * из-за этого объяснение владельцу незачем.
   */
  const DEPLOY_PHASES = new Set([
    'prepare', 'services', 'backup', 'import', 'finalize', 'restart', 'check',
  ]);
  const deployPhase = phase != null && DEPLOY_PHASES.has(phase);
  const deploying = r.state === 'running' && (deployPhase
    || (r.willDeploy && (filesDone || nothingLeft) && silentFor > 45_000));
  const stalled = r.state === 'running' && begun && !deploying && silentFor > 60_000;
  const preparing = r.state === 'running' && !begun;

  const waited = Math.max(0, Math.round((Date.now() - new Date(r.requestedAt).getTime()) / 1000));
  const waitWord = waited < 90 ? `${waited} с` : `${Math.round(waited / 60)} мин`;
  /*
   * Окно вдвое длиннее, чем у прохода копирования, — и не для красоты.
   *
   * Восстановление идёт толчками с периодом в три-четыре секунды: пак
   * приехал, пак разбирается. Двадцатисекундное окно ловит пять таких циклов,
   * и стрелка всё равно качается на границах; сорок укладывают десять, и
   * число становится читаемым. Форма при этом не прячется — она рядом,
   * столбиками, где ей и место.
   */
  /*
   * `files.done` значит разное в разных кадрах.
   *
   * В кадре записи это «записано файлов» (2 553), в кадре проверочного обхода
   * — «осмотрено записей» (4 210 688). Числа приходят вперемешку, из одного
   * поля, и экран, показывавший последнее пришедшее, дёргался между двумя
   * шкалами с разницей в три порядка.
   *
   * Поэтому счётчик записи помним отдельно: он меняется только в кадрах
   * записи, а обход показываем своей строкой и своим числом.
   */
  const laid = useLastWriting(p, inPrep);

  const recent = useRecentRate(p, 40_000);
  const easedRead = useEased(recent?.read ?? 0, live);
  const easedWrite = useEased(recent?.sent ?? 0, live);

  // Две доли, а не одна: по файлам и по байтам. Полоса рядом показывает ту,
  // что понятнее глазу, а прибор — обе, потому что вопрос «сколько осталось»
  // решается второй, а вопрос «долго ли ещё щёлкать мелочью» — первой.
  /*
   * Два числа приёмника обязаны описывать ОДНУ работу.
   *
   * После сверки он присылает `files.total` — сколько файлов надо записать
   * (2 551 из 4 183 237, дерево-то на месте), — а `bytes.total` оставляет
   * прежним: вес всего снимка, 247 ГБ. Числа описывают разные работы, и
   * экран, поверивший второму, обещает четверо суток там, где остался один
   * файл.
   *
   * Ловим это точно, а не на глазок: их вес байт в байт равен весу манифеста,
   * который мы им и выдали, а файлов они назвали меньше. Совпадение до байта
   * при разошедшихся файлах — это не «почти всё впереди», это несвежее число.
   *
   * Верим тому, что подтверждается ходом: `filesDone` растёт к `filesTotal`,
   * а `bytesWritten` к `bytesTotal` не растёт и не собирается.
   */
  const staleTotal = p.bytesTotal != null && r.plan != null
    && p.bytesTotal === r.plan.bytes
    && laid.total != null && laid.total < r.plan.files;
  const fileShare = laid.total != null && laid.total > 0
    ? Math.min(1, laid.done / laid.total) : null;
  const byteShare = !staleTotal && p.bytesTotal != null && p.bytesTotal > 0
    ? Math.min(1, (p.bytesUploaded || p.bytesRead) / p.bytesTotal) : null;

  const byFiles = laid.total != null && laid.total > 0;
  const byBytes = !byFiles && !staleTotal && p.bytesTotal != null && p.bytesTotal > 0;
  const share = byFiles ? Math.min(1, laid.done / laid.total!)
    : byBytes ? Math.min(1, p.bytesUploaded / p.bytesTotal!) : 0;
  const easedShare = useEased(share, live);
  const known = byFiles || byBytes;

  /*
   * Два числа или одно — решает приёмник, а не подпись на экране.
   *
   * «Скачано» и «записано» отвечают на разные вопросы: сколько байт прошло по
   * сети (сжатых, каждый уникальный кусок один раз) и сколько байт легло на
   * диск (распакованных, дубли — столько раз, сколько их в дереве). Замерено
   * на этом самом восстановлении: 11,2 МБ/с из хранилища против 22,1 МБ/с на
   * диск, то есть ровно вдвое.
   *
   * Но приёмник шлёт сейчас ОДНО число в обоих полях. Показывать его дважды
   * под разными подписями — значит утверждать, что сеть и диск идут вровень,
   * чего не бывает. Пока поля совпадают, число одно и подпись честная.
   */
  /*
   * Сеть и диск — из новых полей, если приёмник их шлёт (216).
   *
   * Старая пара `read`/`uploaded` была одним числом в двух полях; новая —
   * действительно два разных: `net` это сжатые байты из сети, `written` —
   * распакованные на диск. Разница на боевом снимке около двух, потолок 3,12.
   */
  const net = p.bytesNet ?? null;
  const written = p.bytesWritten ?? null;
  const twoNumbers = net != null && written != null
    ? net > 0 && written > 0
    : p.bytesUploaded > 0 && p.bytesRead !== p.bytesUploaded;
  const ratio = net != null && written != null && net > 0
    ? written / net
    : (twoNumbers && p.bytesRead > 0 ? p.bytesUploaded / p.bytesRead : null);

  /*
   * Остаток — по недавней скорости, а не по средней с начала.
   *
   * Средняя тащит за собой всё, что было: две-три минуты подготовки, когда не
   * шло ни байта, и медленное начало. Через полчаса работы она всё ещё
   * помнила простой и обещала двенадцать часов там, где по текущей выходило
   * четыре. Прогноз, который врёт втрое, хуже отсутствующего: по нему
   * планируют день.
   *
   * Чтобы не прыгать с часа на сутки на мелких файлах, берём недавнюю
   * скорость в БАЙТАХ (она ровнее файловой) и падаем на среднюю, пока
   * недавней ещё нет.
   */
  const elapsed = r.startedAt ? (now - new Date(r.startedAt).getTime()) / 1000 : 0;
  const doneBytes = p.bytesUploaded || p.bytesRead;
  const totalBytes = staleTotal ? 0 : p.bytesTotal ?? 0;
  const avgRate = elapsed > 20 && doneBytes > 0 ? doneBytes / elapsed : 0;
  // Средняя — второй стрелкой. Сглаживаем её так же, как текущую: иначе одна
  // стрелка живая, а вторая дёргается ступеньками при каждом кадре.
  const easedAvg = useEased(avgRate, live);
  /*
   * Форма потока, а не только его величина.
   *
   * Измерено на этом самом восстановлении: из 57 секунд 43 — с нулём байт.
   * Приёмник качает пак на 67 МБ за секунду, три секунды его разбирает, и по
   * кругу; сеть и диск простаивают по очереди. Средняя скорость об этом не
   * говорит ничего, а прибор со сглаживанием — тем более: он показывает
   * ровную стрелку там, где ровного нет.
   */
  /*
   * Форму считаем по сети, если сеть известна.
   *
   * Иначе прибор врёт в самом выводе: он говорил «канал стоит», а канал в этот
   * момент тянул 14,8 МБ/с — просто счётчик записанного двигается рывками,
   * когда собирается крупный файл. Ряд по записанному тоже полезен, но
   * называть его каналом нельзя.
   */
  const flowSource = net ?? doneBytes;
  const flowIsNet = net != null;
  const flow = useFlowShape(flowSource, r.state === 'running');
  // Прогноз считается по МЕДЛЕННОЙ скорости: пятиминутное окно и медиана.
  // По двадцатисекундной он скакал с часов на сутки и обратно на каждом
  // большом файле — по такому числу нельзя планировать день, а именно для
  // этого его и читают.
  const slow = useSlowRate(doneBytes, r.state === 'running');
  const rate = slow && slow > 0 ? slow : avgRate;
  const rawLeft = rate > 0 && totalBytes > doneBytes ? (totalBytes - doneBytes) / rate : null;
  // И само число ещё раз сглаживаем: даже медиана двигается ступеньками, а
  // «осталось» должно уменьшаться, а не мигать.
  const easedLeft = useEased(rawLeft ?? 0, live, 0.08);
  const left = rawLeft == null ? null : (easedLeft > 0 ? easedLeft : rawLeft);
  const leftBasis = slow ? 'по скорости за последние минуты' : 'по средней с начала';

  /*
   * Подготовка — отдельный экран, а не половина прибора.
   *
   * Раньше на её месте оставалась разметка от спидометра: узкая колонка слева,
   * широкая справа, и две полосы на разной высоте. Выглядело сломанным, и
   * справедливо: это другая работа с другими величинами, и мерить её надо
   * своими числами, а не занимать чужой каркас.
   *
   * Здесь всё, что известно на этой минуте, в одном ряду и на одной высоте:
   * чем занят, как быстро, сколько прошёл, что предстоит и до какого часа
   * выдан ключ.
   */
  if (preparing) {
    const share = p.filesTotal != null && p.filesTotal > p.filesDone
      ? p.filesDone / p.filesTotal : null;
    /*
     * Чьё число называть, пока идёт подготовка.
     *
     * Приёмник читает два перечня — снимка и свой, с диска — и сверяет их,
     * только когда прочитаны оба. Пока он читает, его `bytes.total` это вес
     * снимка целиком: 247 ГБ, как будто диск пуст. Мы же помним прошлый
     * разворот в этот каталог и знаем верхнюю границу лучше: 33 ГБ.
     *
     * Поэтому берём меньшее из двух и говорим, чьё оно. Наше — верхняя
     * граница по памяти; их — измерение, и как только сверка его даст, оно
     * окажется меньше и вытеснит наше само.
     *
     * Иначе выходило то, на что владелец и указал: две минуты подряд красная
     * строка «не хватает 83 ГБ» при работе на тридцать три гигабайта.
     */
    /*
     * Лучшее из трёх, и порядок не произволен.
     *
     * `bytes.pending` приёмника — измерение, идущее ПРЯМО СЕЙЧАС: он сверяет и
     * на ходу говорит, сколько отнёс к записи. Это самый точный ответ, какой
     * бывает, и он уточняется на глазах.
     *
     * Наше `expectBytes` — память о прошлом развороте, верхняя граница.
     *
     * Их `bytes.total` до конца сверки — вес снимка целиком, то есть ответ на
     * вопрос «влезет ли снимок на пустой диск». Диск не пуст, и именно поэтому
     * его выбрали.
     */
    const measured = p.bytesPending ?? null;
    const theirs = p.bytesTotal ?? null;
    const mine = r.expectBytes ?? null;
    const guess = theirs == null ? mine : mine == null ? theirs : Math.min(theirs, mine);
    const need = measured ?? guess;
    const ours = measured == null && need != null && theirs != null && need < theirs;
    const elapsed = agoWords(now - new Date(r.startedAt ?? r.requestedAt).getTime())
      .replace(' назад', '');
    return (
      <div className="prep">
        <div className="prep__head">
          <span className="prep__phase">{phaseWord ?? 'готовится'}</span>
          <span className="prep__elapsed">{elapsed}</span>
        </div>

        <div className="prep__body">
          <div className="prep__rate">
            {recent && recent.files > 0
              ? <><b>{count(Math.round(recent.files))}</b><span>записей/с</span></>
              : <span className="muted">считает…</span>}
          </div>

          <div className="prep__track">
            <div className="run__bar" role="img"
                 aria-label={share != null
                   ? `осмотрено ${Math.round(share * 100)} процентов`
                   : 'идёт, доля неизвестна'}>
              {share != null
                ? <span className="run__fill" style={{ width: `${Math.round(share * 100)}%` }} />
                : <span className="run__fill run__fill--indeterminate" />}
            </div>
            <div className="prep__count">
              <b>{count(p.filesDone)}</b>
              {share != null && <span className="muted"> из {count(p.filesTotal!)}</span>}
              <span className="muted"> · {counterWord(phase, false)}</span>
            </div>
            {/* Подробность приёмника — одной строкой и только здесь. */}
            {p.note && <div className="prep__note muted">{p.note}</div>}
          </div>
        </div>

        <div className="prep__facts muted">
          <span>
            {rechecking
              ? 'проверка после записи: опись снимается заново, план сверяется — до четырёх кругов'
              : phase === 'compare'
                ? 'сверка идёт по диску и сеть не трогает: ноль скорости здесь — работа, а не заминка'
                : 'байты пойдут после подготовки'}
          </span>
          <span>
            предстоит {ours ? 'не больше ' : ''}{bytes(need ?? 0)}
            {/*
              Сколько уже сошлось — рядом. Это самая обнадёживающая цифра
              подготовки: она растёт, пока «предстоит» падает, и по ней видно,
              что сверка идёт, а не стоит.
            */}
            {p.bytesMatched != null && p.bytesMatched > 0 && (
              <> · сошлось {bytes(p.bytesMatched)}</>
            )}
            {' · '}{count(r.plan?.files ?? 0)} файлов
          </span>
          {/*
            Место — здесь, а не только в смете до запуска.

            Смета считает по верхней границе: весь снимок, как будто на диске
            пусто. Приёмник во время сверки узнаёт правду и опускает «предстоит»
            до того, чего у него нет, — и наша строка вместе с ним из красной
            становится зелёной. Это и есть ответ на вопрос «влезет ли», данный
            тем, кто будет писать, а не тем, кто гадает.
          */}
          {r.freeBytes != null && need != null && (
            <span className={r.freeBytes - need > 0 ? 'prep__fit' : 'prep__fit--tight'}>
              место: свободно {bytes(r.freeBytes)}
              {r.freeBytes - need > 0
                ? `, останется ${bytes(r.freeBytes - need)}`
                : ` — не хватает ${bytes(need - r.freeBytes)}`}
              {measured != null
                ? ' (по сверке — уточняется на ходу)'
                : ours ? ' (по прошлому развороту; приёмник назовёт точное после сверки)' : ''}
            </span>
          )}
          <span>
            ключ до {r.expiresAt ? new Date(r.expiresAt).toLocaleTimeString('ru-RU') : '—'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="run__gauge">
      {/*
        Прибор занят целиком — потому что показывать есть что, а пустая
        половина прибора выглядит поломкой. Пока приёмник шлёт одно число
        вместо двух, вторая стрелка занята СРЕДНЕЙ скоростью: те же байты, та
        же шкала, и видно главное — сейчас быстрее обычного или медленнее.
        По краям две доли, и расходятся они втрое: файлов сделано полтора
        процента, а байтов четверть — значит мелочь уже прошла, тяжёлое
        впереди, и остаток надо читать по байтам, а не по файлам.
      */}
      {r.state === 'running' && (
        <SpeedGauge
          read={easedRead}
          sent={twoNumbers ? easedWrite : easedAvg}
          quiet={!live}
          words={twoNumbers
            ? { big: 'в секунду из сети', small: '/с на диск' }
            : { big: 'сейчас', small: '/с в среднем' }}
          matched={fileShare}
          stored={byteShare}
          sideWords={{ left: 'файлов', right: 'байтов' }}
        />
      )}

      <div className="run__right">
        {/*
          Пока приёмник не взялся, бегущей полосы нет.
          Штриховка означает «идёт, сколько — неизвестно», а здесь не идёт
          ничего: ключи выданы, команда отправлена, ответа нет. Полоса,
          бегущая над нулями, обещает работу, которой не начиналось.
        */}
        {r.state === 'granted' ? (
          <p className="run__waiting">
            ключи выданы, ждём приёмник — {waitWord}
            <span className="muted">
              {' '}· если он не возьмётся за восемь минут, консоль отзовёт ключи сама
            </span>
          </p>
        ) : (
          <div className="run__bar" role="img"
               aria-label={known ? `готово ${Math.round(share * 100)} процентов` : 'доля пока неизвестна'}>
            {known
              ? <span className="run__fill" style={{ width: `${Math.round(easedShare * 100)}%` }} />
              : <span className="run__fill run__fill--indeterminate" />}
          </div>
        )}

        <dl className="run__stats">
          <div>
            {/*
              Подпись описывает ТО ЖЕ, что число под ней.

              Число здесь — запомненный счётчик записи, а подпись бежала за
              живой фазой и превращалась в «записей осмотрено» над числом
              записанных файлов. Владелец: «опять гонка за экран». Круг
              проверки виден своей строкой ниже, и подписи для него тут не
              нужно.
            */}
            <dt>{moved ? 'файлов' : counterWord(phase, false)}</dt>
            <dd>
              {/*
                Больше итога не показываем. Приёмник на проверочном круге
                дописал два файла и прислал «2 553 из 2 551»: своё же число он
                не поправил, а «сделано больше, чем всего» читается как поломка
                экрана.
              */}
              {count(Math.min(laid.done, laid.total ?? laid.done))}
              {byFiles && <span className="muted"> из {count(laid.total!)}</span>}
            </dd>
          </div>
          {/*
            В подготовке «восстановлено 0 из 247 ГБ» и «осталось —» отвечают
            на вопрос, которого никто не задавал: байты ещё не шли, и ноль тут
            не новость, а пустое место. Вместо них — что предстоит.
          */}

          {twoNumbers ? (
            <>
              <div>
                <dt>из сети</dt>
                <dd>{bytes(net ?? p.bytesRead)}</dd>
              </div>
              <div>
                <dt>на диск</dt>
                <dd>
                  {bytes(written ?? p.bytesUploaded)}
                  {/*
                    Отношение показываем в ту сторону, в какую оно на самом
                    деле. Больше единицы — выигрыш дедупликации: на байт из
                    сети легло несколько байт файла. Меньше — перекос: сеть
                    возит больше, чем ложится, и это не «×0,0», а «в 20 раз
                    больше», иначе главное число дня выглядит опечаткой.
                  */}
                  {ratio != null && ratio >= 1 && (
                    <span className="muted"> · ×{ratio.toFixed(1)} на байт из сети</span>
                  )}
                </dd>
              </div>
            </>
          ) : (
            <div>
              <dt>восстановлено</dt>
              <dd title="Приёмник шлёт одно число на сеть и на диск, поэтому и показываем одно">
                {bytes(p.bytesUploaded || p.bytesRead)}
                {!staleTotal && (
                  <span className="muted"> из {bytes(p.bytesTotal ?? 0)}</span>
                )}
              </dd>
            </div>
          )}
          <div>
            <dt>осталось</dt>
            {deploying ? (
              <dd title="Приёмник не сообщает о ходе развёртывания">
                идёт развёртывание
                <span className="muted"> · заливка базы у него занимает около
                  полутора часов</span>
              </dd>
            ) : staleTotal ? (
              <dd title="Приёмник назвал число файлов к записи, а вес прислал от всего снимка — по нему считать нельзя">
                {count(Math.max(0, (laid.total ?? 0) - laid.done))} файлов
                <span className="muted"> · во времени не считаем: вес приёмник прислал от всего снимка</span>
              </dd>
            ) : (
              <dd title={`Остаток ${leftBasis}`}>
                {left != null
                  ? <>примерно {forHuman(left)}<span className="muted"> · {leftBasis}</span></>
                  : <span className="muted">пока не из чего считать</span>}
              </dd>
            )}
          </div>
        </dl>

        {/*
          Проверочный круг — строкой внутри работы, а не подменой экрана.

          Приёмник после записи снимает опись заново и так до четырёх кругов.
          Это часть той же работы: байты записаны, ключи те же, счётчик записи
          стоит на месте не потому, что что-то сломалось, а потому, что сейчас
          читают, а не пишут.
        */}
        {rechecking && (
          <p className="muted run__prep">
            Проверочный круг: приёмник снимает опись заново и сверяет с
            планом — до четырёх кругов.{' '}
            {p.filesDone > 0 && <>Осмотрено {count(p.filesDone)}
              {p.filesTotal ? ` из ${count(p.filesTotal)}` : ''}.</>}
            {' '}Записанное при этом не меняется: сейчас читают, а не пишут.
          </p>
        )}

        {/*
          Прерывание видно отдельно от молчания.
          
          «Молчит» означает «не знаем, что там»; здесь знаем: процесс приёмника
          сменился, работа та же, ключи оставлены нарочно. Показать это как
          тревогу значило бы звать чинить исправное.
        */}
        {r.interruptedAt && (
          <p className="muted run__prep">
            Приёмник перезапустился и поднимет эту работу сам — тикет и ключи
            оставлены. Если он не вернётся, сторож закроет восстановление через
            пятнадцать минут молчания и отзовёт ключи.
          </p>
        )}

        {deploying && !r.interruptedAt && (
          <div className="run__deploy">
            <p className="run__deploy-head">
              <b>Файлы разложены — идёт развёртывание.</b>{' '}
              {phaseWord ?? 'приёмник поднимает проект'}
              {/*
                Счётчик шагов показываем, только когда он похож на счётчик шагов.

                Их 280: в поле штук однажды приехали байты заливки — «3 770 397 499
                файлов», полсотни строк обратным счётом. Число было настоящее, поле
                чужое. Они это починили, но правило дешевле починки: шагов у
                развёртывания десятки, а не миллиарды, и «шаг 3 770 397 499 из 0»
                нельзя показывать ни при какой чужой ошибке.
              */}
              {p.stepTotal != null && p.stepTotal > 0 && p.stepTotal < 1000
                && (p.stepDone ?? 0) <= p.stepTotal && (
                <span className="muted"> · шаг {count(p.stepDone ?? 0)} из {count(p.stepTotal)}</span>
              )}
            </p>
            {/*
              Доля заливки — единственная настоящая на этом этапе.

              Дамп льётся байтами, и приёмник их считает (274). Прежде здесь
              стояло обещание «около полутора часов», а обещание не движется.
            */}
            {p.importTotal != null && p.importTotal > 0 && (
              <>
                <div className="run__bar" role="img"
                     aria-label={`залито ${Math.round(
                       ((p.importWritten ?? 0) / p.importTotal) * 100)} процентов дампа`}>
                  <span className="run__fill" style={{
                    width: `${Math.round(((p.importWritten ?? 0) / p.importTotal) * 100)}%`,
                  }} />
                </div>
                <p className="muted">
                  залито {bytes(p.importWritten ?? 0)} из {bytes(p.importTotal)}
                </p>
              </>
            )}
            {p.note && <p className="muted">{p.note}</p>}
            {!deployPhase && (
              <p className="muted">
                Приёмник об этих шагах пока не сообщает — сборка на нём старая.
                Молчит {agoWords(silentFor).replace(' назад', '')}; пока он на
                связи, сторож работу не тронет.
              </p>
            )}
          </div>
        )}

        {stalled && !r.interruptedAt && (
          <p className="alert alert--error">
            Приёмник молчит {agoWords(silentFor).replace(' назад', '')}. Ход
            присылают каждые две секунды, и такая пауза — это не тяжёлый файл.
            {/*
              Про сторожа говорим ровно то, что он делает. Он закрывает работу
              по молчанию ТОЛЬКО у того, кто ещё и пропал со связи: отобрать
              ключ у работающего дороже, чем подождать. Обещать закрытие через
              пятнадцать минут значило бы пугать тем, чего не будет.
            */}
            {' '}Пока приёмник на связи, сторож её не тронет; пропадёт и он —
            закроет через пятнадцать минут и отзовёт ключ на чтение. Начать
            заново можно той же кнопкой: уже скачанное не перекачивается.
          </p>
        )}

        {/*
          Перекос — отдельной строкой и красным, потому что это не мелочь.
          Замерено на боевом: 4,8 ГБ из сети ради 237 МБ на диске, при
          одиннадцати паках, скачанных повторно из семидесяти одного. Смета,
          посчитанная по записанному, обещает при этом недели.
        */}
        {/*
          Про паки говорим только когда объём делает разговор осмысленным.

          Пак весит 67 МБ и приезжает целиком. Если записать надо два мегабайта
          — а на повторном развороте так и бывает, — то «использовано 1%»
          арифметически верно и по смыслу пусто: меньше одного пака не
          скачаешь. Красная строка про перекос над работой, которой всё удалось,
          пугает там, где хвалить надо.
        */}
        {ratio != null && ratio < 0.9 && written != null && written > 2e9 && (
          <p className="alert alert--error">
            {/*
              Полезная доля пака — самое говорящее число этого экрана.
              Замерено на боевом: 9,19 ГБ из сети за 136 паков — то есть по
              67,6 МБ, ровно целыми паками, — а на диск легло 382 МБ. Значит из
              каждого пака пригодилось 4%: остальное приехало и было
              отброшено. По замыслу так быть не должно вовсе: передаём мы
              минимум, а на диск из одного куска пишем несколько копий, и
              записано никогда не должно быть меньше переданного.
            */}
            Из каждого пака используется {(ratio * 100).toFixed(0)}% —
            остальное приезжает и отбрасывается
            {p.packsRefetched != null && p.packsDone != null && p.packsRefetched > 0
              ? `; ${p.packsRefetched} паков из ${p.packsDone} скачаны повторно`
              : ''}. По замыслу на диск должно ложиться <b>больше</b>, чем
            приезжает: передаётся минимум, а дубли пишутся из одного куска.
          </p>
        )}

        {flow.series.length >= 4 && (
          <div className="run__flow">
            <Sparkline values={flow.series} label="скорость по кадрам за последние минуты" />
            <p className="muted run__flow-word">
              {flow.idleShare != null && flow.idleShare > 0.25
                ? (flowIsNet
                  ? <>поток рваный: {Math.round(flow.idleShare * 100)}% времени канал стоит
                    {' '}— приёмник качает пак целиком, потом разбирает его</>
                  : <>записывается рывками: {Math.round(flow.idleShare * 100)}% времени счётчик
                    {' '}не двигается. Это про диск, а не про сеть: сетевые байты приёмник
                    {' '}пока не сообщает, и стоит ли канал — отсюда не видно</>)
                : <>{flowIsNet ? 'канал загружен ровно' : 'записывается ровно'}
                  {flow.idleShare != null ? ` — пауз ${Math.round(flow.idleShare * 100)}%` : ''}</>}
            </p>
          </div>
        )}

        <div className="run__foot">
          {p.currentPath && (
            <p className="run__path mono" title={p.currentPath}>
              {/*
                Имя источника рядом с путём. Источники разворачиваются по
                очереди — код, медиа, журналы, дампы, — и они разной природы:
                у кода тысячи мелких файлов, у медиатеки сотни тысяч крупных.
                Не назвав источник, экран заставляет догадываться по пути,
                почему числа вдруг стали другими.
              */}
              {sourceOf(p.currentPath, sourcePaths ?? null)}
              {sourceOf(p.currentPath, sourcePaths ?? null) ? ' · ' : ''}
              {p.currentPath}
            </p>
          )}
          <p className="run__pulse">
            {r.state === 'running'
              /*
                Слово приёмника показываем ВСЕГДА, когда оно есть.
                
                Раньше оно доставалось только подготовке, а в работе строка
                сокращалась до «последнее движение сейчас». Владелец на это и
                указал: «нет никакой активности, визуально тишина» — в тот
                самый момент, когда приёмник честно сообщал «спрашиваю, где
                лежат чанки». Тишина была наша, а не его: единственное
                объяснение происходящего мы прятали.
              */
              ? (begun
                ? `последнее движение ${agoWords(silentFor)}`
                  + (p.note ? ` · ${p.note}` : '')
                // В подготовке считаем время самой подготовки, а не «движение»:
                // движения там нет по устройству, и говорить о нём — пугать.
                : `${phaseWord ?? 'готовится'} · ${agoWords(
                  now - new Date(r.startedAt ?? r.requestedAt).getTime()).replace(' назад', '')}`
                  + (p.note ? ` · ${p.note}` : ''))
              : `ключи до ${r.expiresAt ? new Date(r.expiresAt).toLocaleTimeString('ru-RU') : '—'}`}
          </p>
        </div>
      </div>
    </div>
  );
}

export function Restore(): React.JSX.Element {
  const { data: agents } = useQuery<AgentsSnapshot>('agents.list');
  const { data: runs, reload: reloadRuns } = useQuery<{ items: RestoreView[] }>('restore.runs');
  const [live, setLive] = useState<Record<string, RestoreView>>({});

  /*
   * Живые события, а не опрос по таймеру.
   *
   * Восстановление идёт часами, и смотреть на него будут именно тогда, когда
   * оно идёт. Опрос раз в несколько секунд дал бы дёрганые числа и нагрузку
   * ровно в тот момент, когда машина занята делом.
   */
  const event = useTopic<RestoreEvent>(RESTORES_TOPIC);
  useEffect(() => {
    const e = event.last;
    if (!e) return;
    setLive((prev) => ({ ...prev, [e.restore.restoreId]: e.restore }));
  }, [event.last, event.seq]);

  /*
   * Связь машин — живая, а не снятая при открытии страницы.
   *
   * Список приёмников закрывает пункты у тех, кто не на связи: развернуть туда
   * нельзя. Но выбирают приёмник не сразу — сначала ищут снимок, смотрят
   * состав, считают место. За эти минуты машина успевает и вернуться, и
   * отвалиться, а список показывал бы то, что было при открытии: закрытый
   * пункт у вернувшейся машины и открытый у пропавшей — со второго начинается
   * восстановление, которое не начнётся.
   */
  const agentsLive = useTopic<AgentEvent>(AGENTS_TOPIC);
  const [presence, setPresence] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const e = agentsLive.last;
    if (!e) return;
    if (e.kind === 'agent.presence') {
      setPresence((prev) => ({ ...prev, [e.kid]: e.online }));
      return;
    }
    // Идёт проход — значит, машина на связи, что бы ни говорил прежний снимок.
    if (e.kind !== 'agent.changed') setPresence((prev) => ({ ...prev, [e.kid]: true }));
  }, [agentsLive.seq]);

  /*
   * Выбор переживает перезагрузку страницы.
   *
   * Визард — это пять решений подряд: сайт, снимок, приёмник, каталог,
   * источники. Полная перезагрузка (а она случается: обновили сборку, упало
   * соединение, нажали F5) стирала их все, и человек начинал сначала — притом
   * что ни одно из решений не устарело.
   *
   * В памяти браузера, а не в адресе: снимок и каталог — это черновик, а не
   * место, на которое дают ссылку. И не на сервере: чужой черновик в общей
   * базе однажды переехал бы в чужой запуск.
   */
  const [site, setSite] = useState<{ hostId: string; siteId: string } | null>(
    () => draft<{ hostId: string; siteId: string } | null>('site', null),
  );
  const [snaps, setSnaps] = useState<SnapshotRow[] | null>(null);
  const [snapshotId, setSnapshotId] = useState<string | null>(
    () => draft<string | null>('snapshotId', null),
  );
  const [sources, setSources] = useState<string[]>(() => draft<string[]>('sources', []));
  const [target, setTarget] = useState<string>(
    () => draft<string>('target', ''),
  );
  const [into, setInto] = useState<string>(
    () => draft<string>('into', ''),
  );
  const [custom, setCustom] = useState(false);
  const [restart, setRestart] = useState(false);
  const [importDumps, setImportDumps] = useState(false);
  const [mode, setMode] = useState<'resume' | 'mirror' | 'prune'>(
    () => draft<'resume' | 'mirror' | 'prune'>('mode', 'resume'),
  );
  /*
   * Что приёмник обязан оставить как есть.
   *
   * Два капкана, найденные на бумаге и подтверждённые делом: `env.php` с
   * адресом чужой базы, который досинхронизация «починит» обратно, и
   * страховочный дамп, который зеркало сочтёт лишним и удалит. Оба молчаливые:
   * магазин поднимется и будет смотреть не туда.
   *
   * Страховочные дампы отмечены по умолчанию — это наша с приёмником
   * договорённость (083), а не особенность чьего-то проекта. Остальное
   * называет владелец: путь, который держат местным, знает он, а не мы.
   */
  const [keep, setKeep] = useState<string[]>(
    () => draft<string[]>('keep', ['.dumps/before-restore-*']),
  );
  const [keepAdd, setKeepAdd] = useState('');
  /*
   * Что не грузить в базу. Пусто — «ещё не выбирали», а не «грузить всё».
   *
   * Разница существенна: предложение приёмника приходит вместе со сметой, и
   * умолчания надо проставить один раз при его появлении, а потом не трогать —
   * иначе снятая владельцем галочка возвращалась бы на каждый пересчёт.
   */
  const [skip, setSkip] = useState<string[] | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * Что именно мы только что запустили.
   *
   * Приёмник вправе отказаться начинать, и отказ приходит через секунду —
   * событием, а не ответом на вызов. Без этой памяти экран вёл себя ровно так,
   * как пожаловался владелец: код второго фактора принят, на мгновение
   * что-то мелькнуло, и снова кнопка. Причина при этом лежала в списке ниже,
   * где её никто не искал: смотрят туда, откуда только что нажали.
   */
  const [startedId, setStartedId] = useState<string | null>(null);
  /** Какую работу разбираем. Пусто — разбор закрыт. */
  const [reportOf, setReportOf] = useState<string | null>(null);
  const [counting, setCounting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const { data: totp } = useQuery<{ active: boolean }>('totp.status');

  // Черновик пишется на каждое изменение: разбираться, какое из пяти решений
  // «главное», незачем — они равны, и потерять любое одинаково обидно.
  useEffect(() => {
    keepDraft({ site, snapshotId, target, into, sources, mode, keep });
  }, [site, snapshotId, target, into, sources.join('|'), mode, keep.join('|')]);
  useEffect(() => {
    if (skip != null || !plan?.skippable?.length) return;
    setSkip(plan.skippable.filter((x) => x.byDefault).map((x) => x.id));
  }, [plan?.skippable?.length]);

  const confirm = useConfirm();

  /**
   * Показать причину, а не «не получилось».
   *
   * Отказ протокола приходит с полем `msg`; ошибка в самом экране — обычным
   * исключением. Прежняя редакция читала только первое, и любая наша же
   * оплошность превращалась в бессмысленную красную строку, по которой нельзя
   * ни понять причину, ни пожаловаться внятно.
   */
  const say = (err: unknown): void => {
    const rpc = (err as { msg?: string; code?: string }).msg;
    const plain = err instanceof Error ? err.message : String(err);
    setFailure(rpc ?? (plain && plain !== '[object Object]' ? plain : 'не получилось'));
  };

  // Один список на весь экран: признак связи подмешан к снимку в одном месте,
  // иначе половина экрана знала бы новость, а половина нет.
  const machines = (agents?.agents ?? []).map((a) => ({
    ...a, online: presence[a.kid] ?? a.online,
  }));

  /*
   * Список берём по снимкам, а не по сегодняшнему манифесту машины.
   *
   * 23.08 манифест прода подменился манифестом приёмника, и боевой сайт с
   * девятью снимками пропал из выбора — притом что все девять лежали в
   * хранилище нетронутыми. Восстановление нужно как раз тогда, когда на машине
   * что-то не так; строить его список из того, что эта машина сейчас про себя
   * говорит, — значит терять его в единственный нужный момент.
   */
  const { data: restorable } = useQuery<{ items: SiteRow[] }>('restore.sites');
  const sites = restorable?.items ?? [];

  /*
   * Поднять черновик до рабочего состояния.
   *
   * Из памяти браузера возвращается только выбор — список снимков живёт на
   * сервере и после перезагрузки пуст. Без этого экран показывал бы выбранный
   * сайт и пустоту под ним: выбор как будто есть, а работать нечем.
   *
   * Заодно проверяем, что сайт вообще ещё в списке. Черновику может быть
   * неделя; сайт за это время могли снять с копирования, и предлагать
   * восстановить то, чего в выборе больше нет, — значит вести к отказу
   * сервера вместо честного «выберите заново».
   */
  useEffect(() => {
    if (!site || snaps || busy || !restorable) return;
    const known = sites.some((x) => x.hostId === site.hostId && x.siteId === site.siteId);
    if (!known) {
      setSite(null); setSnapshotId(null); setSources([]);
      return;
    }
    void (async () => {
      setBusy(true);
      try {
        const r = await request<{ snapshots: SnapshotRow[] }>(
          'snapshot.list', { hostId: site.hostId, siteId: site.siteId },
        );
        const list = (r.snapshots ?? []).filter((x) => x.state === 'complete');
        setSnaps(list);
        // Снимок мог уйти по сроку хранения: держаться за исчезнувший — то же
        // самое, что держаться за исчезнувший сайт.
        setSnapshotId((id) => (id && list.some((x) => x.snapshotId === id) ? id : null));
      } catch (err) { say(err); } finally { setBusy(false); }
    })();
  }, [restorable]);

  async function pickSite(hostId: string, siteId: string): Promise<void> {
    setSite({ hostId, siteId });
    /*
     * Приёмник по умолчанию — та же машина.
     *
     * Частый случай не переезд, а откат: «перезапиши прод копией от вчера».
     * Заставлять выбирать машину, когда в девяти случаях из десяти нужна та
     * же самая, — это лишний шаг, на котором ошибаются.
     */
    setTarget(hostId);
    setRestart(false);
    setImportDumps(false);
    setSnapshotId(null); setPlan(null); setSnaps(null); setFailure(null);
    setBusy(true);
    try {
      const r = await request<{ snapshots: SnapshotRow[] }>('snapshot.list', { hostId, siteId });
      setSnaps((r.snapshots ?? []).filter((s) => s.state === 'complete'));
    } catch (err) { say(err); } finally { setBusy(false); }
  }

  /*
   * Пересчёт сам, а не по кнопке.
   *
   * Кнопка «Посчитать» перекладывала на человека работу машины: он менял
   * галочку и должен был вспомнить, что цифры под ней устарели. Устаревшая
   * смета рядом со свежими галочками хуже отсутствующей — по ней принимают
   * решение.
   *
   * Задержка нужна из-за пути: он набирается по букве, и запрос на каждую
   * букву — это запрос на каждую букву.
   */
  useEffect(() => {
    if (!snapshotId || !target) { setPlan(null); return; }
    const t = setTimeout(() => { void recount(); }, 350);
    return () => clearTimeout(t);
    // Режим здесь не для полноты: «начисто» сперва освобождает содержимое
    // каталога, «не трогая лишнее» не освобождает ничего — и смета из-за этого
    // отличается на всё, что в каталоге лежит.
  }, [snapshotId, target, into, mode, sources.join('|')]);

  async function recount(): Promise<void> {
    if (!snapshotId || !target) return;
    setCounting(true); setFailure(null);
    try {
      const got = await request<Plan>('restore.plan', {
        snapshotId, targetHostId: target, mode,
        // Каталог важен уже здесь: место считается у того тома, которому он
        // принадлежит, а не «по машине вообще».
        ...(into.trim() ? { into: into.trim() } : {}),
        ...(sources.length > 0 ? { sources } : {}),
      });
      setPlan({ ...got, mode });
      // Размеры источников консоль узнаёт при первом же подсчёте — подставляем
      // их в список, чтобы галочки перестали быть выбором вслепую.
      if (got.sources.length > 0) {
        const sizes = got.sources.map((x) => ({
          id: x.id, bytes: x.bytes, ...(x.files != null ? { files: x.files } : {}),
        }));
        setSnaps((prev) => (prev ?? []).map((s) =>
          s.snapshotId === snapshotId ? { ...s, sourceSizes: sizes } : s));
      }
    } catch (err) { setPlan(null); say(err); } finally { setCounting(false); }
  }

  async function launch(): Promise<void> {
    if (!snapshotId || !target || !into) return;
    setBusy(true); setFailure(null);
    try {
      // Опасная операция: сервер спросит код второго фактора сам, окно кода
      // общее на всё приложение.
      // «Очистить» — это тот же полный проход, только с разрешением удалять.
      // Отдельного режима в протоколе нет намеренно: удаление это ключ, а не
      // способ разворачивать.
      const started = await request<{ restoreId: string }>('restore.start', {
        ...(keep.length > 0 && mode !== 'prune' ? { keep } : {}),
        snapshotId, targetHostId: target, into,
        // Четыре ответа на экране — две ручки в протоколе: чем разворачивать
        // и можно ли удалять. Заводить четыре режима значило бы объяснять
        // приёмнику то, что он и так выводит из двух полей.
        mode: mode === 'resume' || mode === 'mirror' ? 'resume' : 'full',
        ...(sources.length > 0 ? { sources } : {}),
        prune: mode === 'prune' || mode === 'mirror',
        // Только для своей машины: сервер это проверяет отдельно, экран может
        // врать, а это команда останавливать контейнеры и заменять базу.
        // Решает план, а не «своя ли машина»: сервер проверяет то же самое
        // ещё раз, и расхождение между экраном и сервером здесь дороже всего.
        /*
         * Плоский список образцов имён, а не названия групп.
         *
         * Их правило из 278, и оно верное: тикет должен говорить о том, что
         * реально произойдёт с базой. Через месяц по нему будут объяснять,
         * почему в журнале поиска пусто, — и «search-log» этого не объяснит.
         */
        ...(importDumps && (skip?.length ?? 0) > 0
          ? {
            skipTables: (plan?.skippable ?? [])
              .filter((x) => skip!.includes(x.id))
              .flatMap((x) => x.tables),
          }
          : {}),
        restart: (plan?.deploy.allowed ?? false) && restart,
        importDumps: (plan?.deploy.allowed ?? false) && importDumps,
      });
      setStartedId(started.restoreId);
      // Выбор НЕ стираем. Он и есть ответ на вопрос «что сейчас разворачивается»,
      // и убирать его в момент запуска значит отвечать «ничего» ровно тогда,
      // когда ответ нужнее всего.
      reloadRuns();
    } catch (err) { say(err); } finally { setBusy(false); }
  }

  async function stop(restoreId: string): Promise<void> {
    setBusy(true);
    try { await request('restore.cancel', { restoreId }); reloadRuns(); }
    catch (err) { say(err); } finally { setBusy(false); }
  }

  const chosen = snaps?.find((s) => s.snapshotId === snapshotId) ?? null;

  /*
   * Что из снимка берётся — один ответ на весь экран.
   *
   * Пустой список источников означает «все»: так понимает метод, и так
   * переключатели не залипают в состоянии «выбрано ровно всё, но списком».
   * Раскладка ниже спрашивала снимок, а не выбор, и показывала media даже
   * тогда, когда владелец её выключил: смета говорила 10 ГБ, а список путей
   * обещал 237 — расхождение ровно в том месте, по которому себя проверяют.
   */
  const takes = (id: string): boolean => sources.length === 0 || sources.includes(id);
  const takenPaths = (chosen?.sourcePaths ?? []).filter((x) => takes(x.id));
  /* Дамп базы — источник без пути: в манифесте у него нет места на диске,
     он раскладывается в `.dumps`. Значит и строка про `.dumps` появляется
     ровно тогда, когда среди выбранного есть хоть один такой. */
  const takenDumps = (chosen?.sources ?? []).filter(
    (id) => takes(id) && !(chosen?.sourcePaths ?? []).some((x) => x.id === id),
  );

  /*
   * Каталоги приёмника — из его же отчёта.
   *
   * Берём из плана, когда он посчитан, иначе из отчёта агента: список нужен
   * ДО подсчёта, потому что без каталога считать нечего.
   */
  /*
   * Каталоги приёмника: сначала то, что он описал специально, потом — корни
   * его проектов.
   *
   * Корни годятся не «за неимением лучшего»: они описаны в манифесте машины,
   * то есть удовлетворяют её же правилу — разворачивать можно только туда, что
   * описано. Пока приёмник не шлёт отдельный список, это единственные пути,
   * про которые мы точно знаем, что они разрешены.
   */
  const targetMachine = machines.find((a) => a.hostId === target) ?? null;
  // Подсказки приёмника: что на этой машине обычно держат местным. Пока он их
  // не шлёт, список пуст — и владелец называет пути сам.
  const keepHints = (targetMachine?.report?.sites ?? [])
    .flatMap((x) => (x as { keepHints?: string[] }).keepHints ?? []);
  // Приёмник могли выбрать десять минут назад, а связь с ним потерять минуту
  // назад. Кнопка должна знать об этом сама: отказ по этой причине приходит
  // уже после подтверждения опасного действия и выглядит как поломка.
  const targetGone = target !== '' && targetMachine?.online === false;
  const report = targetMachine?.report ?? null;
  const dirs = plan?.target.dirs?.length
    ? [...plan.target.dirs].sort((a, b) => {
      // Свои — вперёд. Не фильтруем: машина могла не назвать сайт, а
      // спрятать из-за этого верный каталог хуже, чем показать лишний.
      const mine = (d: { siteId?: string }): number =>
        site && d.siteId === site.siteId ? 0 : d.siteId ? 1 : 2;
      return mine(a) - mine(b);
    })
    : report?.dirs?.length
      ? report.dirs
      // Последний запасной путь: корни сайтов из отчёта. У них нет ни объёма,
      // ни признака занятости — поля объявлены, чтобы список был одной формы,
      // а не тремя разными в зависимости от того, откуда он взялся.
      : (report?.sites ?? []).map((x) => ({
        path: x.root, entries: 0,
      })) as {
        path: string; entries: number; writable?: boolean;
        siteId?: string; why?: string; looksLikeSite?: boolean;
        busy?: { restoreId: string; since: string };
      }[];



  // Список: то, что отдал метод, поверх — то, что пришло живыми событиями.
  // Порядок важен: событие свежее ответа, иначе экран моргал бы назад.
  const rows = Object.values({
    ...Object.fromEntries((runs?.items ?? []).map((r) => [r.restoreId, r])),
    ...live,
  }).sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));

  /*
   * Пока восстановление идёт, форма не редактируется.
   *
   * Не из строгости: поля выше описывают то, что уже запущено, и менять их
   * можно было бы сколько угодно без всякого следствия — но человек, поменявший
   * каталог и увидевший прежний ход работы, вправе решить, что теперь
   * разворачивается в новое место. Замороженная форма отвечает честно: вот что
   * идёт, и другого сейчас не будет.
   */
  /** Разворачиваем туда же, откуда снимали: это откат, а не переезд. */
  const sameMachine = Boolean(site && target && site.hostId === target);

  const busyRestore = rows.find((r) => r.state === 'granted' || r.state === 'running') ?? null;
  const locked = Boolean(busyRestore);

  /*
   * Отказ приёмника — на месте кнопки, а не в списке внизу.
   *
   * Отказ бывает мгновенным: приёмник считает место сам и может не начать
   * вовсе. Тогда работа появляется и исчезает быстрее, чем человек успевает
   * перевести взгляд, и единственный след остаётся строкой в истории.
   */
  const refused = startedId
    ? rows.find((r) => r.restoreId === startedId
      && (r.state === 'failed' || r.state === 'cancelled')) ?? null
    : null;

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Восстановление</h2>
      </header>

      <div className="panel__scroll wizard">
        <p className="wizard__lead muted">
          Разворачивает снимок на <b>другой</b> машине. В выбранном каталоге
          окажется ровно то, что было в папке проекта на исходной: со всеми
          подпапками, как лежало. Дампы баз — в{' '}
          <code className="mono">.dumps</code> в корне проекта. Ничего не
          импортируется: контейнеры поднимаете вы.
        </p>

        {/*
          Шаги — плиткой, а не колонкой.
          Экран широкий, а визард занимал треть его и требовал прокрутки на
          каждом шаге. Разложенные в ряд шаги видны разом: выбранный снимок не
          уезжает из глаз, пока заполняешь каталог, и смету видно вместе с тем,
          что её породило.
        */}
        <div className="wizard__steps">
        {/* ── 1. Что ─────────────────────────────────────────────────── */}
        <section className="wizard__step wizard__step--what">
          <h3 className="wizard__title"><span className="wizard__num">1</span> Что восстанавливаем</h3>
          <div className="wizard__row">
            <select
              className="input"
              disabled={locked}
              value={site ? `${site.hostId}|${site.siteId}` : ''}
              onChange={(e) => {
                const [h, s] = e.target.value.split('|');
                if (h && s) void pickSite(h, s);
              }}
            >
              <option value="">— выберите сайт —</option>
              {sites.map((s) => (
                <option key={`${s.hostId}|${s.siteId}`} value={`${s.hostId}|${s.siteId}`}>
                  {s.siteId} · {s.label}
                  {s.snapshots > 0 ? ` · снимков ${s.snapshots}` : ' · снимков нет'}
                  {/*
                    «Машина его больше не объявляет» — важная строка. Снимки
                    целы и разворачиваются, но копироваться сайт перестал, и
                    узнать об этом лучше здесь, чем через месяц.
                  */}
                  {!s.declared && s.snapshots > 0 ? ' · машина его не объявляет' : ''}
                </option>
              ))}
            </select>
          </div>

          {snaps && snaps.length === 0 && (
            <p className="muted">Готовых снимков у этого сайта нет.</p>
          )}
          {snaps && snaps.length > 0 && (
            <ul className="wizard__snaps">
              {snaps.slice(0, 8).map((s) => (
                <li key={s.snapshotId}>
                  <label className="wizard__snap">
                    <input
                      type="radio" name="snap" disabled={locked}
                      checked={snapshotId === s.snapshotId}
                      onChange={() => { setSnapshotId(s.snapshotId); setSources([]); setPlan(null); }}
                    />
                    <span className="mono">{s.snapshotId.slice(5, 24)}</span>
                    <span className="muted">
                      {s.finishedAt ? new Date(s.finishedAt).toLocaleString('ru-RU') : '—'}
                      {s.totals?.files != null && ` · ${count(s.totals.files)} файлов`}
                      {s.totals?.bytes != null && ` · ${bytes(s.totals.bytes)}`}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          {chosen?.sources && chosen.sources.length > 0 && (
            <div className="wizard__sources">
              <span className="muted">Источники: </span>
              {chosen.sources.map((id) => {
                const size = chosen.sourceSizes?.find((x) => x.id === id);
                const on = takes(id);
                // Выключить последний нельзя: пустой выбор означает «все», и
                // экран отвечал на это тем, что зажигал все переключатели
                // обратно — щелчок, который делает противоположное просьбе.
                const only = on && sources.length === 1;
                return (
                  <span key={id} className="wizard__source"
                        title={only ? 'хотя бы один источник должен остаться' : undefined}>
                    <Toggle
                      checked={on}
                      disabled={locked || only}
                      ariaLabel={`источник ${id}`}
                      onChange={(next) => {
                        const all = chosen.sources ?? [];
                        const now = sources.length === 0 ? [...all] : [...sources];
                        const after = next
                          ? [...new Set([...now, id])]
                          : now.filter((x) => x !== id);
                        // Пусто означает «все»: так метод и понимает, и это
                        // избавляет от состояния «выбрано ровно всё, но
                        // списком» — оно ведёт себя иначе при смене снимка.
                        setSources(after.length === all.length ? [] : after);
                      }}
                    />
                    <span className={on ? '' : 'muted'}>{id}</span>
                    {size && <span className="muted"> {bytes(size.bytes)}</span>}
                  </span>
                );
              })}
              <span className="muted wizard__hint">
                {sources.length === 0 ? 'берём всё' : `выбрано ${sources.length}`}
              </span>
            </div>
          )}
        </section>

        {/* ── 2. Куда ────────────────────────────────────────────────── */}
        <section className="wizard__step wizard__step--where">
          <h3 className="wizard__title"><span className="wizard__num">2</span> Куда разворачиваем</h3>
          {/*
            Шаг пошире и в две колонки — потому что в нём два разных вопроса.
            Слева «куда именно»: машина, каталог и раскладка путей, по которой
            себя проверяют. Справа «что сделать с тем, что там уже есть»: это
            решение, а не адрес, и читать его сверху вниз следом за путями —
            значит каждый раз прокручивать туда-обратно, сверяя одно с другим.
          */}
          <div className="wizard__where">
          <div className="wizard__where-col">
          <div className="wizard__row">
            <select className="input" value={target} disabled={locked}
                    onChange={(e) => { setTarget(e.target.value); setPlan(null); }}>
              <option value="">— выберите приёмник —</option>
              {machines.map((a) => (
                <option key={a.hostId} value={a.hostId} disabled={!a.online}>
                  {a.label} {a.online ? '' : '(не на связи)'}
                  {site && a.hostId === site.hostId ? ' — та же машина, что источник' : ''}
                </option>
              ))}
            </select>
            {/*
              Выбор, а не поле ввода — правило приёмника, а не наше удобство.
              Каталог должен быть заранее описан в манифесте его машины;
              путь, пришедший снаружи, это команда писать на боевом сервере.
              Свободный ввод приводил бы к отказам, причину которых на экране
              не объяснить.
            */}
            {dirs.length > 0 && !custom ? (
              <select className="input wizard__path mono" value={into} disabled={locked}
                      onChange={(e) => {
                        if (e.target.value === '__custom') { setCustom(true); setInto(''); return; }
                        setInto(e.target.value);
                      }}>
                <option value="">— выберите каталог —</option>
                {/*
                  Сначала каталоги ДЛЯ ЭТОГО САЙТА.

                  Приёмник перечисляет не «куда можно писать», а какие копии он
                  готов принять, и называет для каждого каталога сайт. Мы это
                  поле полгода выбрасывали, и владелец выбирал из общего списка:
                  рядом с местом для боевого магазина стояли каталог стенда и
                  корень служебного проекта. Опечатка в таком выборе — это
                  восстановление не туда, и на чужой машине это видно не сразу.
                */}
                {dirs.map((d) => (
                  <option key={d.path} value={d.path} disabled={Boolean(d.busy)}>
                    {d.path}
                    {d.siteId && site && d.siteId === site.siteId ? ' ★ для этого сайта' : ''}
                    {d.siteId && site && d.siteId !== site.siteId ? ` · для ${d.siteId}` : ''}
                    {d.entries > 0 ? ` · ${d.entries} записей` : ' · пусто'}
                    {d.looksLikeSite ? ' · занят проектом' : ''}
                    {d.writable === false ? ' · агент не подтверждает запись' : ''}
                    {/*
                      «Занято сейчас» — не то же, что «писать нельзя». Второе
                      чинят правами, первое ждут или останавливают, и пункт
                      закрыт ровно на время работы.
                    */}
                    {d.busy ? ' · сюда уже идёт восстановление' : ''}
                  </option>
                ))}
                {/* Список не должен запирать: каталог могли описать только что,
                    а отчёт придёт позже. Приёмник всё равно проверит. */}
                <option value="__custom">— другой каталог —</option>
              </select>
            ) : (
              <input
                className="input wizard__path mono"
                placeholder="/media/applications/сайт"
                disabled={locked}
                value={into}
                onChange={(e) => setInto(e.target.value)}
              />
            )}
          </div>
          {plan?.target.topologySuspect && (
            <div className="wizard__swap">
              <p className="wizard__swap-head">
                Машина объявила другую топологию целиком — {plan.target.topologySuspect.why}
              </p>
              <p className="muted">
                Было: <span className="mono">{plan.target.topologySuspect.wasSites.join('; ')}</span>
              </p>
              <p className="muted">
                Стало: <span className="mono">{plan.target.topologySuspect.nowSites.join('; ')}</span>
              </p>
              <p className="muted">
                Так выглядит не переезд, а чужой манифест на этой машине — с
                прод-сервером это уже случалось. Пока не подтвердите, её
                каталоги здесь не предлагаются: она объявит чужой путь готовым
                принять копию, а проверить это её же манифестом нельзя.
              </p>
              <button type="button" className="button button--danger" disabled={busy}
                      onClick={() => confirm.ask({
                        title: 'Это переезд, а не ошибка?',
                        consequences: [
                          'Консоль снова начнёт предлагать каталоги этой машины для записи.',
                          'Подтверждает человек, глядя на две топологии рядом: проверить машину её же манифестом нельзя.',
                        ],
                        confirmLabel: 'Да, топология сменилась намеренно',
                        run: () => void (async () => {
                          try {
                            await request('host.topology.accept', { hostId: target });
                            void recount();
                          } catch (err) { say(err); }
                        })(),
                      })}>
                Это переезд — принять
              </button>
            </div>
          )}

          <p className="muted wizard__hint">
            Каталог проекта на приёмнике — из описанных в манифесте его машины.
            Путь, пришедший снаружи, — это команда писать на боевом сервере, и
            согласие машины должно быть явным; неописанный каталог приёмник
            отвергнет.
            {/*
              Зачем сюда разрешено — словами владельца машины.

              Это единственное место, где о каталоге говорит тот, кто его завёл.
              «Машина принимает копию боевого магазина, каркас развёртывания
              лежит у агента, в дереве приложения наших файлов нет ни одного» —
              такое не выведешь ни из пути, ни из числа записей.
            */}
            {(() => {
              const chosenDir = dirs.find((d) => d.path === into.trim());
              return chosenDir?.why
                ? <p className="muted wizard__hint">{chosenDir.why}</p>
                : null;
            })()}

            {dirs.length > 0 && !plan?.target.dirs?.length && !report?.dirs?.length && (
              <> Пока приёмник не прислал отдельный список, показываем корни его
              проектов — они описаны там же.</>
            )}
          </p>
          {/*
            Показываем раскладку целиком, а не описываем словами.
            Сайт лежит не в корне папки: код у магазина в `app/src`, медиа в
            `app/src/pub/media`. Фраза «то же, что было в исходной папке» это
            правда, но по ней нельзя проверить себя — а по списку путей можно.
          */}
          {into.trim() && chosen?.sourceSizes && chosen.sourceSizes.length > 0 && (
            <div className="wizard__layout mono">
              {takenPaths.map((s2) => (
                <div key={s2.id}>
                  {into.trim().replace(/\/+$/, '')}/{s2.path === '.' ? '' : `${s2.path}/`}
                  <span className="muted">… {s2.id}</span>
                </div>
              ))}
              {takenDumps.length > 0 && (
                <div>
                  {into.trim().replace(/\/+$/, '')}/.dumps/
                  <span className="muted">… {takenDumps.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          <p className="muted wizard__hint">
            {/*
              Возражение снято владельцем и снято по делу: корень проекта —
              служебная папка, сайт живёт в app/src, и дампы оказываются ВЫШЕ
              веб-корня, куда веб-сервер не смотрит. Оставляем оговорку про
              случай, когда проект разложен иначе: она стоит строки.
            */}
            Дампы лежат в корне проекта, то есть выше веб-корня — веб-сервер
            туда не смотрит. Если у проекта раскладка другая и корень раздаётся
            наружу, закройте точечные файлы в конфигурации.
          </p>
          </div>

          <div className="wizard__where-col">
          {/*
            Непустой каталог — отдельный вопрос, а не строчка подсказки.
            Развернуть поверх чужого содержимого можно тремя разными способами,
            и они дают разный результат. Молча выбрать за владельца любой из
            них — значит однажды стереть то, что стирать не просили, или
            оставить то, что должно было исчезнуть.
          */}
          {/*
            Поднимать развёрнутое можно не только у себя.

            Правило «только своя машина» было верным, пока чужая машина
            означала голую копию. Теперь приёмник объявляет проект своим и
            держит каркас развёртывания, который переписывает адреса базы и
            сессий на местные. Это переписывание — вся защита: 27.08 выяснилось,
            что с приёмника боевые адреса ОТВЕЧАЮТ, и копия, поднятая по
            привезённой конфигурации, писала бы в живой прод.

            Решение владельца: разрешаем, но только объявившим. Причину отказа
            показываем всегда — «нельзя» без причины отправляет чинить наугад.
          */}
          {plan?.deploy.allowed && (
            <div className="wizard__same">
              <p className="wizard__same-lead">
                {sameMachine ? (
                  <><b>Та же машина.</b> Это откат: проект переписывается копией
                  из снимка на месте. Можно сразу поднять его обратно.</>
                ) : (
                  <><b>Приёмник готов поднять проект.</b> {plan.deploy.why}. Каркас
                  развёртывания на его стороне переписывает адреса базы и сессий
                  на местные, поэтому копия не потянется в боевые.</>
                )}
              </p>
              <span className="wizard__source">
                <Toggle
                  checked={restart} disabled={locked}
                  ariaLabel="перезапустить контейнеры"
                  onChange={setRestart}
                />
                перезапустить контейнеры после распаковки
              </span>
              <span className="wizard__source">
                <Toggle
                  checked={importDumps} disabled={locked}
                  ariaLabel="загрузить дампы в базы"
                  onChange={setImportDumps}
                />
                загрузить дампы в базы
              </span>
              {/*
                Что не грузить — предлагает приёмник, решает владелец.

                Он видел дамп и знает вес и назначение таблиц; мы не знаем ни
                того, ни другого. Поэтому список его, умолчания его, а слова
                «зачем» показываем целиком: по ним и ставят галочку. Без них
                владелец отмечает вслепую, а «не залито 3,1 ГБ» через месяц
                выглядит поломкой.
              */}
              {importDumps && (plan?.skippable?.length ?? 0) > 0 && (
                <div className="wizard__skip">
                  <p className="wizard__skip-lead">
                    <b>Приёмник предлагает не грузить часть данных.</b> Дамп
                    проигрывается построчно, и час заливки — это в основном они.
                    Отмеченное не попадёт в базу; таблицы при этом создаются
                    пустыми, а не исчезают.
                  </p>
                  {plan!.skippable.map((x) => (
                    <label key={x.id} className="wizard__skip-item">
                      <Toggle
                        checked={(skip ?? []).includes(x.id)} disabled={locked}
                        ariaLabel={`не грузить ${x.id}`}
                        onChange={(on) => setSkip((prev) => {
                          const now = prev ?? [];
                          return on ? [...now, x.id] : now.filter((k) => k !== x.id);
                        })}
                      />
                      <span>
                        <b>{x.id}</b>
                        {x.bytes != null && <span className="muted"> · {bytes(x.bytes)}</span>}
                        <span className="wizard__skip-why muted"> {x.why}</span>
                      </span>
                    </label>
                  ))}
                  {(() => {
                    const saved = plan!.skippable
                      .filter((x) => (skip ?? []).includes(x.id))
                      .reduce((a, x) => a + (x.bytes ?? 0), 0);
                    return saved > 0 ? (
                      <p className="muted">Не будет залито {bytes(saved)}.</p>
                    ) : null;
                  })()}
                </div>
              )}

              {importDumps && (
                <p className="wizard__danger">
                  {sameMachine ? (
                    <>Боевая база будет <b>заменена</b> содержимым дампа из снимка.
                    Всё, что записано в неё после снимка, исчезнет — заказы,
                    оплаты, регистрации. Отменить это будет нечем.</>
                  ) : (
                    <>База <b>на приёмнике</b> будет заменена дампом из снимка.
                    Боевую это не затрагивает — при условии, что каркас
                    развёртывания там переписал адреса. Если он этого не сделал,
                    дамп зальётся туда, куда смотрит привезённая конфигурация, а
                    смотрит она в прод.</>
                  )}
                </p>
              )}
            </div>
          )}

          {/*
            Отказ — там же, где было бы разрешение, и с причиной приёмника.
          */}
          {plan && !plan.deploy.allowed && into.trim() && (
            <p className="muted wizard__hint">
              <b>Базу и службы здесь поднять нельзя:</b> {plan.deploy.why}. Файлы
              лягут все, включая дамп в <code className="mono">.dumps/</code>, —
              но дампом он и останется.
            </p>
          )}

          {/*
            Чем сайт обеспечен здесь и там.
            
            Главный вопрос переезда, и он не про файлы: на проде база — отдельный
            сервер, на приёмнике будет контейнер. Восстановленный проект
            описывает магазин БЕЗ базы, потому что на проде её в проекте и не
            было. Файлы лягут все до одного, а служба, в которую смотрит
            настройка, на новом месте просто не существует.
          */}
          {plan?.services && (plan.services.missing.length > 0 || plan.services.differs.length > 0) && (
            <div className={plan.services.missing.length > 0 ? 'wizard__swap' : 'wizard__keep'}>
              <p className="wizard__swap-head">
                {plan.services.missing.length > 0
                  ? `Приёмник не объявляет: ${plan.services.missing.join(', ')}`
                  : 'Службы обеспечены по-разному'}
              </p>
              {plan.services.differs.map((d) => (
                <p key={d.id} className="muted">
                  <span className="mono">{d.id}</span>: у источника {d.source}, у приёмника {d.target}
                </p>
              ))}
              {plan.services.missing.length > 0 && (
                <p className="muted">
                  Разворачивать можно — файлы лягут, — но подниматься магазину
                  будет нечем: службы, объявленной на источнике, на приёмнике
                  нет ни контейнером, ни внешней. Это чинится в манифесте
                  приёмника, а не здесь.
                </p>
              )}
            </div>
          )}

          {plan?.intoEntries != null && plan.intoEntries > 0 && (
            <p className="wizard__dirty">
              В каталоге уже <b>{plan.intoEntries}</b>{' '}
              {plural(plan.intoEntries, 'запись', 'записи', 'записей')}. Решите,
              что с ними делать.
            </p>
          )}
          {plan?.intoEntries === 0 && (
            <p className="muted wizard__hint">Каталог пуст — развернём начисто.</p>
          )}

          <div className="wizard__row wizard__modes">
            {/*
              Три ответа, а не четыре.
              «Развернуть» и «дописать изменившееся» были одним и тем же:
              разворачивание и так разностное — совпавшее не качается заново, а
              оборванное продолжается с места обрыва. Два названия одного
              действия заставляли владельца выбирать между ними, и выбор этот
              был ни о чём.
            */}
            {([
              ['resume', 'развернуть, не трогая лишнее',
                'Скачает то, чего нет или что изменилось; совпавшее не тронет, оборванное '
                + 'продолжит с места обрыва. Чужие файлы останутся как были. Этим же режимом '
                + 'досинхронизируют перед переключением.'],
              ['mirror', 'дописать и удалить лишнее',
                'То же самое плюс удаление всего, чего нет в снимке. Каталог станет точной '
                + 'копией проекта, но без перекачивания совпавшего.'],
              ['prune', 'очистить каталог и развернуть начисто',
                'Всё, чего нет в снимке, удаляется, остальное скачивается заново. Дольше '
                + 'зеркала, зато не полагается на сверку.'],
            ] as [string, string, string][]).map(([id, label, hint]) => (
              <label key={id} className={`wizard__mode${mode === id ? ' wizard__mode--on' : ''}`}>
                <input
                  type="radio" name="restore-mode" disabled={locked}
                  checked={mode === id}
                  onChange={() => setMode(id as typeof mode)}
                />
                <span>
                  <b>{label}</b>
                  <span className="muted"> — {hint}</span>
                </span>
              </label>
            ))}
          </div>

          {/*
            Список неприкосновенного — рядом с режимами, а не отдельным
            шагом: он отвечает на тот же вопрос «что будет с тем, что уже
            лежит», только с другой стороны. В режиме «начисто» он
            бессмыслен — каталог всё равно очищается, — и там его нет.
          */}
          {mode !== 'prune' && (
            <div className="wizard__keep">
              <div className="wizard__keep-head">Не трогать при разворачивании</div>
              {[...new Set([...keepHints, ...keep])].map((path) => (
                <label key={path} className="wizard__source">
                  <Toggle
                    checked={keep.includes(path)}
                    disabled={locked}
                    ariaLabel={`не трогать ${path}`}
                    onChange={(on) => setKeep((prev) => (on
                      ? [...new Set([...prev, path])]
                      : prev.filter((x) => x !== path)))}
                  />
                  <span className="mono">{path}</span>
                </label>
              ))}
              <div className="wizard__row">
                <input
                  className="input wizard__path mono"
                  placeholder="ещё путь — например, magento2/app/etc/env.php"
                  value={keepAdd}
                  disabled={locked}
                  onChange={(e) => setKeepAdd(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || !keepAdd.trim()) return;
                    setKeep((prev) => [...new Set([...prev, keepAdd.trim()])]);
                    setKeepAdd('');
                  }}
                />
                <button type="button" className="button" disabled={locked || !keepAdd.trim()}
                        onClick={() => {
                          setKeep((prev) => [...new Set([...prev, keepAdd.trim()])]);
                          setKeepAdd('');
                        }}>
                  Добавить
                </button>
              </div>
              <p className="muted wizard__hint">
                Перечисленное приёмник не скачивает, не удаляет и расхождением
                не считает. Сюда идёт то, что на приёмнике местное: настройки с
                адресом его базы, страховочные дампы. Забыть путь — значит
                молча переписать боевую настройку копией чужой.
              </p>
            </div>
          )}
          </div>
          </div>
        </section>

        {/* ── 3. Смета ───────────────────────────────────────────────── */}
        <section className="wizard__step wizard__step--sum">
          <h3 className="wizard__title">
            <span className="wizard__num">3</span> Сколько это весит
            {counting && <span className="muted wizard__counting">считаю…</span>}
          </h3>

          {!plan && (
            <p className="muted">Выберите снимок и приёмник — посчитается само.</p>
          )}

          {plan && (
            <>
              <div className="wizard__plan">
                <div><span className="muted">нужно</span> <b>{bytes(plan.needBytes)}</b></div>
                <div>
                  <span className="muted">займёт</span>{' '}
                  {plan.estimate
                    ? (
                      <>
                        <b>примерно {forHuman(plan.estimate.seconds)}</b>
                        <span className="muted">
                          {' '}— {plan.estimate.basis}, {bytes(plan.estimate.bytesPerSec)}/с
                        </span>
                      </>
                    )
                    : <span className="muted">оценить не из чего</span>}
                </div>
                <div className="muted">
                  файлов {count(plan.totals.files)}, каталогов {count(plan.totals.dirs)},
                  чанков {count(plan.totals.chunks)}
                </div>
              </div>

              {/*
                Ёмкость приёмника — отдельным показателем, а не строкой в смете.
                Вопрос «влезет ли» решает, начинать ли вообще, и отвечать на
                него надо до того, как человек дошёл до красной кнопки.
              */}
              <Capacity plan={plan} />
            </>
          )}
        </section>

        {/*
          Просьбы машин — выше формы, а не ниже.

          Их немного, и они срочные: код живёт полчаса, а человек у машины ждёт
          ответа прямо сейчас. Ниже формы владелец до них не доходит — он идёт
          на этот экран разворачивать, а не читать.
        */}
        <RestoreRequests />

        {/* ── 4. Пуск ────────────────────────────────────────────────── */}
        <section className="wizard__step wizard__step--go">
          <h3 className="wizard__title"><span className="wizard__num">4</span> Запуск</h3>

          {locked && (
            <p className="wizard__locked">
              Идёт восстановление <b>{busyRestore!.siteId}</b> в{' '}
              <code className="mono">{busyRestore!.into}</code>. Пока оно не
              закончится, форма заморожена: поля выше описывают то, что уже
              запущено, и менять их — значит показывать одно, а делать другое.
            </p>
          )}

          {totp && !totp.active && (
            <p className="wizard__2fa">
              <b>Второй фактор не привязан.</b> Восстановление — запись поверх
              чужой машины, и подтверждать её должен человек, а не тот, у кого
              оказался ключ. Привяжите телефон на вкладке «Ключи доступа»: это
              одна минута, и после неё опасные действия будут просить код.
            </p>
          )}

          <p className="muted wizard__hint">
            Приёмнику будет выдан ключ на чтение <b>одного</b> этого снимка, на
            полчаса, с продлением, пока идёт работа. По завершении ключ
            удаляется.{' '}
            {totp?.active
              ? 'Консоль спросит код второго фактора.'
              : 'Кода спрошено не будет — фактор не привязан.'}
          </p>

          {targetGone && (
            <p className="alert alert--error">
              {targetMachine?.label ?? 'Приёмник'} сейчас не на связи. Развернуть
              туда нечем: команду принимает сам агент. Как только он вернётся,
              кнопка откроется — список обновляется сам, страницу перезагружать
              не нужно.
            </p>
          )}

          {refused && (
            <div className="alert alert--error">
              <b>
                {refused.startedAt == null
                  ? 'Приёмник не взялся.'
                  : 'Восстановление остановилось.'}
              </b>{' '}
              {refused.error?.msg ?? 'причина не названа'}
              {refused.error?.code === 'no_space' && (
                <p className="muted">
                  Он считает по весу снимка целиком, ещё не сверив дерево с диском —
                  то самое, от чего мы отказались на этой странице. Если каталог уже
                  развёрнут, писать придётся малую часть, но узнать это можно только
                  сверкой, а сверка начинается после проверки места. Пока приёмник не
                  научится проверять место после сверки, помогает одно из двух:
                  освободить место под полный вес снимка или разворачивать по
                  источникам — отметьте наверху только нужные.
                </p>
              )}
            </div>
          )}

          <button
            type="button" className="button button--danger"
            {...{ /* Кнопку гасит только ТОЧНОЕ «не влезет» — по пустому каталогу.
                     Неточное отрицание сюда не попадает: см. довод в смете. */ }}
            disabled={busy || locked || targetGone || !plan || !into.trim()
              || blocksStart(plan)}
            onClick={() => confirm.ask({
              title: 'Развернуть снимок на приёмнике?',
              consequences: [
                ...(plan && plan.fits === false && !plan.fitsCertain
                  ? ['Свободного места по весу снимка не хватает, но дерево на приёмнике '
                    + 'уже есть: сколько писать, он выяснит сверкой и сам откажется, если не влезет.']
                  : []),
                `На ${plan?.target.label ?? 'приёмник'} в каталог ${into.trim()} ляжет `
                + `${bytes(plan?.needBytes ?? 0)}: содержимое проекта как на источнике, дампы баз в .dumps/.`,
                mode === 'resume'
                  ? 'Скачается недостающее и изменившееся; совпавшее не тронется, чужое останется.'
                  : mode === 'mirror'
                    ? 'Каталог станет точной копией: изменившееся скачается, лишнее УДАЛИТСЯ.'
                    : 'Каталог будет ОЧИЩЕН: всё, чего нет в снимке, удалится безвозвратно.',
                ...(plan?.deploy.allowed && restart
                  ? ['Контейнеры проекта будут перезапущены после распаковки.'] : []),
                ...(plan?.deploy.allowed && importDumps
                  ? ['БОЕВАЯ БАЗА БУДЕТ ЗАМЕНЕНА дампом из снимка. Всё, что записано в '
                    + 'неё после снимка, исчезнет: заказы, оплаты, регистрации.'] : []),
                ...(importDumps && (skip?.length ?? 0) > 0
                  ? [`В базу НЕ попадёт: ${(plan?.skippable ?? [])
                    .filter((x) => skip!.includes(x.id)).map((x) => x.id).join(', ')}. `
                    + 'Таблицы будут созданы пустыми; данные из них не восстановятся '
                    + 'сами — только новой заливкой без этих галочек.'] : []),
                'Приёмник получит ключ на чтение этого снимка на полчаса.',
              ],
              ...(totp?.active
                ? {}
                : { instead: 'Привязать второй фактор на вкладке «Ключи доступа» — '
                    + 'тогда такие действия будут подтверждаться кодом.' }),
              confirmLabel: 'Развернуть',
              // Ввод имени — только когда заменяется база. Не для строгости:
              // пока набираешь «eurobrands-shop.de», успеваешь понять, чью
              // именно базу сейчас заменишь.
              ...(plan?.deploy.allowed && importDumps
                ? { typeToConfirm: site?.siteId ?? '' } : {}),
              run: () => { void launch(); },
            })}
          >
            Развернуть на {plan?.target.label ?? 'приёмник'}
          </button>
        </section>

        </div>

        {failure && <p className="alert alert--error">{failure}</p>}

        {/* ── что уже идёт и что было ────────────────────────────────── */}
        {rows.length > 0 && (
          <section className="wizard__step">
            <h3 className="wizard__title">Восстановления</h3>
            <ul className="restores">
              {rows.map((r) => (
                <li key={r.restoreId} className={`restores__item restores__item--${r.state}`}>
                  {/*
                    Разбор открывается с самой строки, а не с кнопки рядом.
                    Строка и есть работа; спрашивают о ней целиком — «что там
                    было», — а не о каком-то её поле.
                  */}
                  <button type="button" className="restores__open"
                          title="Открыть разбор этой работы"
                          onClick={() => setReportOf(r.restoreId)}>
                    <span className="restores__site">{r.siteId}</span>
                  </button>
                  <span className="mono restores__into">{r.into}</span>
                  <span className="mono muted" title="Снимок, который разворачивается">
                    {r.snapshotId.slice(5, 24)}
                  </span>
                  <span className="restores__state">{STATE_WORD[r.state] ?? r.state}</span>
                  <span className="muted">
                    {/*
                      Слово под числом решает фаза: в сверке `files.done` это
                      ОСМОТРЕННЫЕ записи, а не записанные файлы. Строка «4 194 304
                      файлов записано» у работы, записавшей один, — неправда о
                      чужой машине, а не мелкая неточность.
                    */}
                    {tell(r.progress, { count, bytes }).text}
                  </span>
                  {r.mismatches > 0 && (
                    <span className="wizard__bad">не сошлись суммы: {r.mismatches}</span>
                  )}
                  {r.error && <span className="wizard__bad">{r.error.msg}</span>}
                  {(r.state === 'granted' || r.state === 'running') && (
                    <button type="button" className="button button--ghost button--small button--danger"
                            disabled={busy} onClick={() => void stop(r.restoreId)}>
                      Остановить
                    </button>
                  )}
                  {(r.state === 'running' || r.state === 'granted') && (
                    <div className="restores__watch"><RestoreProgress r={r}
                                     sourcePaths={r.snapshotId === chosen?.snapshotId
                                       ? chosen?.sourcePaths ?? null : null} /></div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <Confirm
        open={Boolean(confirm.pending)}
        title={confirm.pending?.title ?? ''}
        consequences={confirm.pending?.consequences ?? []}
        {...(confirm.pending?.instead ? { instead: confirm.pending.instead } : {})}
        confirmLabel={confirm.pending?.confirmLabel ?? 'Подтвердить'}
        {...(confirm.pending?.typeToConfirm
          ? { typeToConfirm: confirm.pending.typeToConfirm } : {})}
        busy={busy}
        onConfirm={() => { const run = confirm.pending?.run; confirm.close(); void run?.(); }}
        onCancel={confirm.close}
      />
      {reportOf && (
        <RestoreReport restoreId={reportOf} onClose={() => setReportOf(null)} />
      )}
    </section>
  );
}
